import braze from "@braze/web-sdk";
import { UserProps } from "@customer-tools-web/component-authentication";
import { isMobile } from "@customer-tools-web/component-ui";

import { usePlatform } from "../../hooks";

const keyUnreadCount = "braze.content.impressionCount";
const keyLeadCount = "braze.content.newLeadCount";
const keyLastUpdate = "braze.content.lastUpdated";
const leadEnquiryTypes = ["ldm-enquiry", "email-enquiry"];

export const useBraze = () => {
  const keyDisabled = "braze.disabled";
  const { isMobilePlatform } = usePlatform();

  // have another localstorage setting to disabled braze to prevent test automation from being blocked by braze IAM
  if (
    process.env.REACT_APP_FEATURE_BRAZE !== "on" ||
    isMobilePlatform ||
    localStorage.getItem(keyDisabled) === "1"
  ) {
    return;
  }
  return {
    init,
    login,
    markContentCardAsRead,
    logContentCardsDisplayed,
    getCachedContentCards,
    getContentUnreadCount,
    getContentLastUpdated,
    getNewLeadCount,
    setNewLeadCount,
  };
};

function init() {
  // @ts-ignore
  window.appboy = braze;
  braze.initialize(String(process.env.REACT_APP_BRAZE_API_KEY), {
    baseUrl: String(process.env.REACT_APP_BRAZE_URL),
    minimumIntervalBetweenTriggerActionsInSeconds: 0,
    enableLogging: process.env.NODE_ENV === "development",
    allowUserSuppliedJavascript: true,
  });

  setupInAppMessage();
  setupContentCard();
}

function login(viewer: UserProps["viewer"]) {
  // do not show in app if internal login as agent
  if (!viewer.permissions.isLoginAsAgent) {
    // always trigger changeUser and openSession so that session will get refresh after timeout
    // braze itself will check for same user/valid session
    braze.changeUser(viewer.lockeUserName);
    braze.openSession();
  }
}

function setupInAppMessage() {
  braze.subscribeToInAppMessage((inAppMessage) => {
    if (inAppMessage instanceof braze.InAppMessage) {
      const extras = inAppMessage.extras;
      let displayInApp = true;
      if (extras != null) {
        const ippProSetting = extras.ippProDisplay;
        if (
          (ippProSetting === "mobile" && !isMobile(window.innerWidth)) ||
          (ippProSetting === "desktop" && isMobile(window.innerWidth))
        ) {
          displayInApp = false;
        }
      }
      if (displayInApp) {
        braze.display.showInAppMessage(inAppMessage);
      }
    }
  });
}

function contentCardCalledBack(cards: braze.ContentCards) {
  const unreadCount =
    cards?.cards?.filter(
      (x) => x.extras?.type === "general" && x.viewed === false
    )?.length || 0;
  if (getContentUnreadCount() !== unreadCount) {
    // manually dispatch event so different component in same page will update the unread count in header
    localStorage.setItem(keyUnreadCount, String(unreadCount));
    dispatchEvent("refreshContentCardUnRead");
  }
  if (getContentLastUpdated() !== String(cards.lastUpdated)) {
    // manually dispatch event so different component in same page will update the card content
    localStorage.setItem(keyLastUpdate, String(cards.lastUpdated));
    dispatchEvent("refreshContentCardContent");
  }
  const emailEnquiry: braze.Card[] = cards?.cards?.filter((x) =>
    leadEnquiryTypes.includes(x.extras?.type)
  );
  if (emailEnquiry?.length > 0) {
    setNewLeadCount(1);
    if (emailEnquiry?.length > 1) {
      emailEnquiry.shift();
      for (const item of emailEnquiry) {
        braze.logCardDismissal(item);
      }
    }
  }
}

function setupContentCard() {
  braze.subscribeToContentCardsUpdates(contentCardCalledBack);
}

function markContentCardAsRead(card: braze.Card) {
  // fake a url to unblock the client side checking: "Card "+a.id+" has no url. Not logging click to Braze servers."
  const cardToClick = card as any;
  if (!cardToClick.url) {
    cardToClick.url = "#";
  }
  braze.logCardClick(cardToClick, true);
  // force reload so the green read dot will disappear
  forceReloadContent();
}

function logContentCardsDisplayed(cards: braze.Card[]) {
  if (cards.length > 0) {
    const notViewed = cards?.filter((x) => x.viewed === false)?.length;
    braze.logContentCardsDisplayed();
    braze.logCardImpressions(cards, true);
    if (notViewed > 0 || getContentUnreadCount() !== notViewed) {
      // if some card is mark as read, refresh content card so the badge number will be updated
      braze.requestContentCardsRefresh();
    }
  }
}

function getCachedContentCards() {
  const content = braze.getCachedContentCards();
  if (content) {
    content.cards = content.cards.filter((x) => x.extras?.type === "general");
  }
  return content;
}

function getContentUnreadCount() {
  const count = localStorage.getItem(keyUnreadCount);
  return Number(count);
}

function getNewLeadCount(): number {
  const count = localStorage.getItem(keyLeadCount);
  return Number(count);
}

function setNewLeadCount(count: number) {
  localStorage.setItem(keyLeadCount, String(count));
  dispatchEvent("refreshLeadIcon");
  if (count === 0) {
    const contents = braze
      .getCachedContentCards()
      ?.cards?.filter((x) => leadEnquiryTypes.includes(x.extras?.type));
    for (const item of contents) {
      braze.logCardDismissal(item);
    }
    // manual flush request to avoid when there is multiple card dismissal action happen, braze tend to hold it, causing the green dot re-appear after re-login
    if (contents?.length > 0) {
      braze.requestImmediateDataFlush();
    }
  }
}

function getContentLastUpdated() {
  return localStorage.getItem(keyLastUpdate);
}

function forceReloadContent() {
  localStorage.setItem(keyLastUpdate, "now");
  contentCardCalledBack(braze.getCachedContentCards());
}

function dispatchEvent(eventName: string) {
  const event = new UIEvent(eventName);
  window.dispatchEvent(event);
}
